import { useState, useEffect, useRef, useCallback } from 'react';
import Editor, { OnMount } from '@monaco-editor/react';
import {
  Folder, FolderOpen, File, ChevronRight, ChevronDown, X, Plus, Save,
  RefreshCw, Search, Download, Upload, Loader2, Box, GitBranch, FileCode2,
} from 'lucide-react';
import { useStore } from '../../store/useStore';
import { api } from '../../services/api';
import type { Project } from '../../types';

interface CodeEditorProps {
  projectId?: string;
  title?: string;
}

interface EditorFile {
  path: string;
  content: string;
  dirty: boolean;
  savedVersion: number;
}

interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  children?: FileNode[];
}

export function CodeEditorApp({ projectId, title }: CodeEditorProps) {
  const currentProject = useStore((s) => s.currentProject);
  const addNotification = useStore((s) => s.addNotification);
  const settings = useStore((s) => s.settings);
  const openApp = useStore((s) => s.openApp);

  const activeProject: Project | null = projectId
    ? (currentProject?.id === projectId ? currentProject : ({ id: projectId, name: title || 'Project' } as Project))
    : currentProject;

  const [fileTree, setFileTree] = useState<FileNode[] | null>(null);
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set());
  const [tabs, setTabs] = useState<EditorFile[]>([]);
  const [activePath, setActivePath] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ file: string; line: number; content: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const editorRef = useRef<any>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncingRef = useRef(false);

  const loadProjectFiles = useCallback(async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const res = await api.getProjectFiles(activeProject.id);
      setFileTree(res.tree || res);
      setExpandedDirs((prev) => new Set([...(prev || []), '']));
    } catch (err: any) {
      addNotification('Error', err.message || 'Failed to load project files', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeProject?.id]);

  useEffect(() => {
    if (activeProject) {
      loadProjectFiles();
    }
  }, [activeProject?.id]);

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  const buildTree = (paths: string[]): FileNode[] => {
    const root: Record<string, any> = {};
    for (const fullPath of paths) {
      const parts = fullPath.split('/');
      let node = root;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (i === parts.length - 1) {
          node[part] = { path: fullPath, type: 'file' };
        } else {
          if (!node[part]) node[part] = { path: parts.slice(0, i + 1).join('/'), type: 'dir', children: {} };
          node = node[part].children;
        }
      }
    }
    const toNodes = (obj: Record<string, any>): FileNode[] =>
      Object.entries(obj)
        .sort(([a], [b]) => {
          const aIsDir = obj[a].type === 'dir';
          const bIsDir = obj[b].type === 'dir';
          if (aIsDir !== bIsDir) return aIsDir ? -1 : 1;
          return a.localeCompare(b);
        })
        .map(([name, entry]) =>
          entry.type === 'file'
            ? { name, path: entry.path, type: 'file' as const }
            : { name, path: entry.path, type: 'directory' as const, children: toNodes(entry.children) }
        );
    return toNodes(root);
  };

  const openFile = async (path: string) => {
    if (!activeProject) return;
    const existing = tabs.find((t) => t.path === path);
    if (existing) {
      setActivePath(path);
      return;
    }
    try {
      const res = await api.getProjectFile(activeProject.id, path);
      const content = res.content ?? '';
      setTabs((prev) => [...prev, { path, content, dirty: false, savedVersion: 0 }]);
      setActivePath(path);
    } catch (err: any) {
      addNotification('Error', `Failed to open ${path}: ${err.message}`, 'error');
    }
  };

  const closeTab = (path: string) => {
    const tab = tabs.find((t) => t.path === path);
    if (tab?.dirty) {
      if (!confirm(`Save changes to "${path}" before closing?`)) {
        if (confirm(`Discard changes to "${path}"?`)) {
          doClose(path);
        }
        return;
      }
      saveFile(tab);
    }
    doClose(path);
  };

  const doClose = (path: string) => {
    setTabs((prev) => prev.filter((t) => t.path !== path));
    setActivePath((prev) => {
      if (prev !== path) return prev;
      const idx = tabs.findIndex((t) => t.path === path);
      const remaining = tabs.filter((t) => t.path !== path);
      return remaining[Math.min(idx, Math.max(remaining.length - 1, 0))]?.path || null;
    });
  };

  const saveFile = async (file: EditorFile) => {
    if (!activeProject || syncingRef.current) return;
    try {
      syncingRef.current = true;
      await api.updateProjectFile(activeProject.id, file.path, file.content);
      setTabs((prev) => prev.map((t) => (t.path === file.path ? { ...t, dirty: false, savedVersion: t.savedVersion + 1 } : t)));
      addNotification('Success', `Saved ${file.path}`, 'success');
    } catch (err: any) {
      addNotification('Error', `Save failed: ${err.message}`, 'error');
    } finally {
      syncingRef.current = false;
    }
  };

  const saveActive = () => {
    const active = tabs.find((t) => t.path === activePath);
    if (active && active.dirty) {
      saveFile(active);
    }
  };

  const handleEditorChange = (value: string | undefined) => {
    if (!activePath) return;
    setTabs((prev) => prev.map((t) => (t.path === activePath ? { ...t, content: value ?? '', dirty: true } : t)));

    if (settings.autoSave) {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        const active = tabs.find((t) => t.path === activePath);
        if (active) saveFile({ ...active, content: value ?? '' });
      }, settings.autoSaveDelay);
    }
  };

  const handleEditorMount: OnMount = (editor) => {
    editorRef.current = editor;
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !activeProject) return;
    setSearching(true);
    setSearchResults([]);
    try {
      // Search within already-open tabs first
      const localResults: { file: string; line: number; content: string }[] = [];
      for (const tab of tabs) {
        const lines = tab.content.split('\n');
        lines.forEach((line, i) => {
          if (line.toLowerCase().includes(searchQuery.toLowerCase())) {
            localResults.push({ file: tab.path, line: i + 1, content: line.slice(0, 200) });
          }
        });
      }
      setSearchResults(localResults.slice(0, 200));
    } finally {
      setSearching(false);
    }
  };

  const getLanguage = (path: string) => {
    const ext = path.split('.').pop()?.toLowerCase();
    const map: Record<string, string> = {
      js: 'javascript', jsx: 'javascript', ts: 'typescript', tsx: 'typescript',
      html: 'html', css: 'css', json: 'json', md: 'markdown', py: 'python',
      sh: 'shell', bash: 'shell', yml: 'yaml', yaml: 'yaml', rs: 'rust',
      go: 'go', java: 'java', c: 'c', cpp: 'cpp', h: 'c', php: 'php',
      sql: 'sql', xml: 'xml', ini: 'ini', toml: 'ini', cfg: 'ini', conf: 'ini',
    };
    return map[ext || ''] || 'plaintext';
  };

  const renderNode = (node: FileNode, depth: number) => {
    const isDir = node.type === 'directory';
    const isExpanded = expandedDirs.has(node.path);
    const isFile = node.type === 'file';
    const canEdit = isFile && /^[^/]+\.[^/]+$/.test(node.path.split('/').pop() || '') && isTextFile(node.name);

    return (
      <div key={node.path}>
        <button
          onClick={() => {
            if (isDir) {
              setExpandedDirs((prev) => {
                const next = new Set(prev);
                if (next.has(node.path)) next.delete(node.path);
                else next.add(node.path);
                return next;
              });
            } else if (canEdit) {
              openFile(node.path);
            }
          }}
          className={`w-full flex items-center gap-1.5 px-2 py-[3px] text-xs rounded-md hover:bg-desktop-hover/60 transition-colors ${activePath === node.path ? 'bg-desktop-accent/15 text-desktop-accent' : 'text-desktop-text/80'}`}
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
          title={node.path}
        >
          {isDir ? (
            <>
              {isExpanded ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
              {isExpanded ? <FolderOpen className="w-3.5 h-3.5 text-amber-400 shrink-0" /> : <Folder className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
            </>
          ) : (
            <>
              <span className="w-3" />
              <File className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            </>
          )}
          <span className="truncate">{node.name}</span>
          {!isDir && isTextFile(node.name) && activePath === node.path && tabs.find((t) => t.path === node.path)?.dirty && (
            <span className="w-1.5 h-1.5 rounded-full bg-desktop-warning ml-auto shrink-0" />
          )}
        </button>
        {isDir && isExpanded && node.children && (
          <div>{node.children.map((child) => renderNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  if (!activeProject) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 bg-desktop-surface2 p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-desktop-surface flex items-center justify-center">
          <FileCode2 className="w-7 h-7 text-desktop-accent" />
        </div>
        <h2 className="text-base font-medium text-desktop-text">No project open</h2>
        <p className="text-sm text-desktop-text/60 max-w-xs">
          Open a project first, or start a new one in the Projects app.
        </p>
        <button
          onClick={() => openApp('projects')}
          className="px-4 py-2 text-sm bg-desktop-accent text-white rounded-lg hover:opacity-90 transition-opacity"
        >
          Open Projects
        </button>
      </div>
    );
  }

  const activeFile = tabs.find((t) => t.path === activePath);

  return (
    <div className="h-full flex bg-desktop-surface2">
      {/* Sidebar */}
      <div className="w-56 shrink-0 border-r border-desktop-border bg-desktop-surface flex flex-col">
        <div className="flex items-center justify-between px-2 py-2 border-b border-desktop-border shrink-0">
          <div className="flex items-center gap-1.5 text-xs text-desktop-text/80">
            <Box className="w-3.5 h-3.5 text-desktop-accent" />
            <span className="truncate max-w-28">{activeProject.name}</span>
          </div>
          <button onClick={loadProjectFiles} className="text-desktop-text/50 hover:text-desktop-text transition-colors" title="Refresh file tree">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-1">
          {loading && !fileTree ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-desktop-accent animate-spin" />
            </div>
          ) : !fileTree || fileTree.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-desktop-text/40">
              <Folder className="w-8 h-8" />
              <p className="text-xs">Empty workspace</p>
            </div>
          ) : (
            <div>
              {renderNode({ name: '', path: '', type: 'directory', children: fileTree }, 0)}
            </div>
          )}
        </div>
        <div className="flex items-center justify-center gap-2 px-2 py-1.5 border-t border-desktop-border shrink-0">
          <button
            onClick={() => openApp('terminal', { projectName: activeProject.name, projectId: activeProject.id })}
            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-desktop-surface2 hover:bg-desktop-hover text-desktop-text/60 hover:text-desktop-text transition-colors"
            title="Open Terminal"
          >
            <Box className="w-3 h-3" /> Terminal
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Tabs bar */}
        <div className="flex items-center bg-desktop-surface border-b border-desktop-border shrink-0 overflow-x-auto">
          {tabs.length === 0 ? (
            <div className="px-3 py-2 text-xs text-desktop-text/40">No files open - click a file to edit</div>
          ) : (
            tabs.map((tab) => (
              <div
                key={tab.path}
                onClick={() => setActivePath(tab.path)}
                className={`group flex items-center gap-1.5 px-2.5 py-2 text-xs cursor-pointer border-r border-desktop-border shrink-0 transition-colors ${
                  activePath === tab.path
                    ? 'bg-desktop-surface2 text-desktop-text'
                    : 'text-desktop-text/60 hover:text-desktop-text'
                }`}
              >
                <File className="w-3 h-3 text-slate-400" />
                <span>{tab.path.split('/').pop()}</span>
                {tab.dirty && <span className="w-1.5 h-1.5 rounded-full bg-desktop-warning" />}
                <button
                  onClick={(e) => { e.stopPropagation(); closeTab(tab.path); }}
                  className="opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity text-desktop-text/60"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-1 px-2 py-1 border-b border-desktop-border bg-desktop-surface shrink-0">
          <button
            onClick={saveActive}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded-md hover:bg-desktop-hover text-desktop-text/70 hover:text-desktop-text transition-colors"
            title="Save (or Cmd/Ctrl+S)"
          >
            <Save className="w-3.5 h-3.5" /> Save
          </button>
          <button
            onClick={() => setSearchOpen((v) => !v)}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${searchOpen ? 'bg-desktop-accent/20 text-desktop-accent' : 'hover:bg-desktop-hover text-desktop-text/70 hover:text-desktop-text'}`}
            title="Search (or Cmd/Ctrl+F)"
          >
            <Search className="w-3.5 h-3.5" /> Search
          </button>
          {settings.autoSave && (
            <span className="ml-auto text-[10px] text-desktop-success flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-desktop-success" /> Auto-save on
            </span>
          )}
        </div>

        {/* Search panel */}
        {searchOpen && (
          <div className="px-3 py-2 border-b border-desktop-border bg-desktop-surface shrink-0 flex gap-2 items-center">
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              placeholder="Search in files..."
              className="flex-1 px-2 py-1 text-xs bg-desktop-surface2 border border-desktop-border rounded-md text-desktop-text focus:outline-none focus:border-desktop-accent"
            />
            <button
              onClick={handleSearch}
              disabled={searching || !searchQuery.trim()}
              className="px-2 py-1 text-xs bg-desktop-accent text-white rounded-md hover:opacity-90 disabled:opacity-50"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-0 max-h-48 overflow-y-auto bg-desktop-surface border border-desktop-border rounded-lg shadow-xl z-10">
                {searchResults.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => { openFile(r.file); }}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-desktop-hover text-desktop-text"
                  >
                    <span className="text-desktop-accent">{r.file}:{r.line}</span>{' '}
                    <span className="text-desktop-text/70">{r.content.trim().slice(0, 80)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Editor */}
        <div className="flex-1 min-h-0">
          {activeFile ? (
            <Editor
              path={`memory://${activeFile.path}`}
              language={getLanguage(activeFile.path)}
              value={activeFile.content}
              onChange={handleEditorChange}
              onMount={handleEditorMount}
              theme={settings.theme === 'dark' ? 'vs-dark' : 'light'}
              options={{
                fontSize: settings.fontSize,
                minimap: { enabled: false },
                fontFamily: 'Menlo, Monaco, Consolas, monospace',
                automaticLayout: true,
                scrollBeyondLastLine: false,
                wordWrap: 'off',
                tabSize: 2,
                renderWhitespace: 'selection',
                smoothScrolling: true,
                suggest: { showMethods: true, showFunctions: true, showFields: true },
                quickSuggestions: true,
                acceptSuggestionOnEnter: 'on',
                snippetSuggestions: 'inline',
                bracketPairColorization: { enabled: true },
                padding: { top: 8 },
                lineHeight: 18,
              }}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-desktop-text/40">
              <FileCode2 className="w-12 h-12" />
              <p className="text-sm">Select a file from the explorer</p>
              <p className="text-xs">Press Ctrl+S to save, Ctrl+F to search</p>
            </div>
          )}
        </div>

        {/* Status bar */}
        <div className="flex items-center justify-between px-3 py-1 border-t border-desktop-border bg-desktop-surface shrink-0 text-[10px] text-desktop-text/50">
          <span>{activeFile ? activeFile.path : 'No file selected'}</span>
          <div className="flex items-center gap-3">
            {activeFile && (
              <>
                <span>{getLanguage(activeFile.path)}</span>
                <span>Ln {editorRef.current?.getPosition?.()?.lineNumber || 1}, Col {editorRef.current?.getPosition?.()?.column || 1}</span>
                {activeFile.dirty && <span className="text-desktop-warning">● Unsaved</span>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function isTextFile(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  const textExts = [
    'txt', 'md', 'js', 'ts', 'jsx', 'tsx', 'html', 'htm', 'css', 'scss', 'less',
    'json', 'xml', 'yml', 'yaml', 'py', 'sh', 'bash', 'zsh', 'rs', 'go', 'java',
    'c', 'h', 'cpp', 'hpp', 'php', 'rb', 'pl', 'lua', 'sql', 'ini', 'cfg', 'conf',
    'env', 'toml', 'vue', 'svelte', 'astro', 'svg', 'log', 'csv', 'tsv', 'graphql',
  ];
  return textExts.includes(ext);
}